import { NgClass, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { smallFontSizeAnimation } from '@app/shared/animations/font-size.animations';
import { AuthService } from '@core/auth/auth.service';
import { SidebarFooterComponent } from '@core/layout/sidebar/sidebar-footer/sidebar-footer.component';
import { SidebarItemComponent } from '@core/layout/sidebar/sidebar-item/sidebar-item.component';
import { SideBarItemConfig } from '@core/layout/sidebar/sidebar-item/sidebar-item.model';
import { PermissionsService } from '@core/permissions/services/permissions.service';
import { CurrentUserService } from '@core/services/current-user.service';
import { filter } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  animations: [smallFontSizeAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, SidebarItemComponent, SidebarFooterComponent, NgOptimizedImage],
})
export class SidebarComponent implements OnInit {
  private readonly _currentUserService = inject(CurrentUserService);
  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _permissionsService = inject(PermissionsService);

  public readonly isOpen = input(true);

  public readonly toggleSidebar = output<void>();

  public activeItem: string = '';
  public administrationMenuConfig: SideBarItemConfig[] = [
    {
      link: '/administration/groups',
      icon: null,
      itemText: 'Groups',
      isShownForUser: false,
    },
    {
      link: '/administration/tags',
      icon: null,
      itemText: 'Tags',
      isShownForUser: false,
    },
  ];

  private readonly routerSegmentsMap: { [key: string]: string } = {
    groups: 'Groups',
    reminders: 'Reminders',
    administration: 'Administration',
    tags: 'Tags',
    'tg-users': 'Telegram Contacts',
    settings: 'System Settings',
  };

  public readonly userData = this._currentUserService.user;

  constructor() {
    this.updateActiveItem();

    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.updateActiveItem());
  }

  ngOnInit(): void {
    this.updateTabsAvailability();
  }

  public isTabAvailable(tab: string): boolean {
    switch (tab) {
      case 'administration':
        return this._permissionsService.hasAdministrationAccess();
      case 'reminders':
        return this._permissionsService.canViewReminders();
      case '/administration/groups':
        return this._permissionsService.hasAdministrationAccess() && this._permissionsService.canViewAdminGroups();
      case '/administration/tags':
        return this._permissionsService.hasAdministrationAccess() && this._permissionsService.canViewTagsCatalog();
      case 'settings':
        return (
          this._permissionsService.canViewTelegramSettings() || this._permissionsService.canConfigureGroupSettings()
        );
      case 'tg-users':
        return this._permissionsService.isAdmin();
      default:
        return false;
    }
  }

  public onLogoutEvent(): void {
    this._authService.logout();
  }

  public setActiveItem(item: string): void {
    this.activeItem = item;
  }

  private updateActiveItem(): void {
    const parsedUrlRoot = this._router.parseUrl(this._router.url).root;
    this.activeItem = parsedUrlRoot.hasChildren()
      ? parsedUrlRoot.children['primary']?.segments
          .map(segment => this.routerSegmentsMap[segment.path])
          .filter(segment => !!segment)
          .join('.')
      : '';

    this._cdr.markForCheck();
  }

  private updateTabsAvailability(): void {
    this.administrationMenuConfig.forEach(item => {
      item.isShownForUser = this.isTabAvailable(item.link);
    });
  }
}
